import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useSEO } from "@/hooks/useSEO";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/Sections";
import { Reveal } from "@/components/Reveal";
import { Field, TextArea } from "@/components/Field";
import { FileUpload } from "@/components/FileUpload";
import { FormSuccess } from "@/components/FormSuccess";
import { CtaPrimary } from "@/components/CTA";

export default function RequestPart() {
  useSEO({ title: "Request a Part | BlackSilex", description: "Tell us what is wearing out. Part number, machine model, manufacturer, quantity and a photo — we respond with availability, origin, lead time and pricing.", path: "/request-part" });
  const [params] = useSearchParams();
  const category = params.get("category");
  const [form, setForm] = useState({ name: "", email: "", part_number: "", machine_model: "", manufacturer: "", quantity: "", current_supplier: "", required_date: "", location: "", notes: category ? `Category of interest: ${category}` : "" });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/part-requests", { ...form, files });
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="request-part-page">
      <PageHeader
        crumbs={[{ label: "Home", to: "/" }, { label: "Parts", to: "/parts" }, { label: "Request a Part" }]}
        eyebrow="Part Request"
        titleLines={["Tell us what", "is wearing out."]}
        intro={category ? `Category: ${category}. Give us the specifics and we will get to work.` : "Give us the specifics of the failing component and we will get to work on a supply response."}
      />

      <section className="max-w-[1100px] mx-auto px-6 lg:px-10 py-16 lg:py-24">
        {done ? (
          <FormSuccess copy="Our team will review the requirement and respond with availability, origin, lead time and pricing." />
        ) : (
          <Reveal>
            <form onSubmit={submit} className="grid md:grid-cols-2 gap-6" data-testid="part-form">
              <Field half label="Name" name="name" value={form.name} onChange={set} required testId="p-name" placeholder="Contact name" />
              <Field half label="Email" name="email" type="email" value={form.email} onChange={set} required testId="p-email" placeholder="you@company.com" />
              <Field half label="Part Number" name="part_number" value={form.part_number} onChange={set} testId="p-partnumber" placeholder="OEM / reference" />
              <Field half label="Machine Model" name="machine_model" value={form.machine_model} onChange={set} testId="p-machine" placeholder="Equipment model" />
              <Field half label="Equipment Manufacturer" name="manufacturer" value={form.manufacturer} onChange={set} testId="p-manufacturer" placeholder="OEM brand" />
              <Field half label="Quantity" name="quantity" value={form.quantity} onChange={set} testId="p-quantity" placeholder="Units required" />
              <Field half label="Current Supplier" name="current_supplier" value={form.current_supplier} onChange={set} testId="p-supplier" placeholder="Existing source" />
              <Field half label="Required Delivery Date" name="required_date" type="date" value={form.required_date} onChange={set} testId="p-date" />
              <Field label="Location" name="location" value={form.location} onChange={set} testId="p-location" placeholder="Site / city" />
              <TextArea label="Additional Notes" name="notes" value={form.notes} onChange={set} testId="p-notes" placeholder="Describe the wear problem, urgency, context…" />
              <div className="md:col-span-2">
                <label className="block tech-label text-mutedgray mb-3">Photo Upload</label>
                <FileUpload value={files} onChange={setFiles} testId="p-files" />
              </div>
              <div className="md:col-span-2 pt-4">
                <CtaPrimary type="submit" testId="p-submit">{busy ? "Submitting…" : "Get a Supply Response"}</CtaPrimary>
              </div>
            </form>
          </Reveal>
        )}
      </section>
    </div>
  );
}
